import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { toast } from '@utils/functions/notificationToast'
import { useUser } from '@hooks/useUser'

import { HIERARCHY_ORDER, HIERARCHY_TITLE, WORKFLOW_STATUS_CONFIG } from './statusConfig'
import { Stepper } from './Stepper'

import type { ApprovalWorkflowBoardProps, WorkflowStatusCode } from './types'
import type { HierarchyStatusItemProps } from '@services/api/previewManagement/types'

import * as apiPreviewManagement from '@services/api/previewManagement'
import * as S from './styles'

const STEPS = ['01', '02', '03', '04']
const SKELETON_ROWS = [0, 1, 2]

export const ApprovalWorkflowBoard = ({
  referenceDate,
  weekNumber,
  filters,
  onOpenUnitsList,
  onSearchResult
}: ApprovalWorkflowBoardProps) => {
  const { userPermissionsData } = useUser.getState()

  const [loadingStatus, setLoadingStatus] = useState<WorkflowStatusCode | null>(null)

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['preview-management-hierarchy', referenceDate, weekNumber, filters],
    queryFn: () =>
      apiPreviewManagement.getWeeklyStatusHierarchy({
        referenceDate: referenceDate!,
        weekNumber: weekNumber ?? undefined,
        ...filters
      }),
    enabled: !!referenceDate
  })

  useEffect(() => {
    if (!isSuccess) return
    onSearchResult?.(!!data)
  }, [isSuccess, data])

  const hierarchies = [...(data?.hierarchies ?? [])].sort(
    (a, b) => HIERARCHY_ORDER.indexOf(a.hierarchyName) - HIERARCHY_ORDER.indexOf(b.hierarchyName)
  )

  const profileCode = userPermissionsData?.profile?.code
  const activeIndex = hierarchies.findIndex((hierarchy) => !!profileCode && hierarchy.profileCodes.includes(profileCode))

  const handleStatusClick = async (status: HierarchyStatusItemProps) => {
    if (!referenceDate || !data) return

    const statusCode = status.statusPreview as WorkflowStatusCode
    const config = WORKFLOW_STATUS_CONFIG[statusCode]

    if (!config) return

    setLoadingStatus(statusCode)

    try {
      const response = await apiPreviewManagement.getStatusPreviewBranches({
        statusPreview: status.statusPreview,
        referenceDate,
        weekNumber: weekNumber ?? data.weekNumber,
        ...filters
      })

      const units = response.rows.map((branch) => branch.branchDescription)

      if (units.length === 0) {
        toast.warning('Nenhum dado encontrado no momento')
        return
      }

      onOpenUnitsList({
        label: config.label,
        icon: config.icon,
        color: config.color,
        background: config.background,
        count: status.branchCount,
        units
      })
    } catch (error) {
      toast.error('Falha ao buscar as unidades do status selecionado!')
    } finally {
      setLoadingStatus(null)
    }
  }

  if (isLoading) {
    return (
      <S.Container>
        <Stepper steps={STEPS} activeIndex={-1} />

        <S.HeaderRow>
          {HIERARCHY_ORDER.map((hierarchyName, index) => (
            <S.HeaderCell key={hierarchyName} isLast={index === HIERARCHY_ORDER.length - 1}>
              {HIERARCHY_TITLE[hierarchyName] ?? hierarchyName}
            </S.HeaderCell>
          ))}
        </S.HeaderRow>

        <S.BodyRow>
          {HIERARCHY_ORDER.map((hierarchyName, index) => (
            <S.BodyColumn key={hierarchyName} isLast={index === HIERARCHY_ORDER.length - 1}>
              {SKELETON_ROWS.map((row) => (
                <S.SkeletonStatusRow key={row}>
                  <S.SkeletonLine width="65%" height="16px" />
                  <S.SkeletonLine width="28px" height="16px" />
                </S.SkeletonStatusRow>
              ))}
            </S.BodyColumn>
          ))}
        </S.BodyRow>
      </S.Container>
    )
  }

  return (
    <S.Container>
      <Stepper steps={STEPS} activeIndex={activeIndex} />

      <S.HeaderRow>
        {hierarchies.map((hierarchy, index) => (
          <S.HeaderCell key={hierarchy.hierarchyName} isLast={index === hierarchies.length - 1}>
            {HIERARCHY_TITLE[hierarchy.hierarchyName] ?? hierarchy.hierarchyName}
          </S.HeaderCell>
        ))}
      </S.HeaderRow>

      <S.BodyRow>
        {hierarchies.map((hierarchy, index) => (
          <S.BodyColumn key={hierarchy.hierarchyName} isLast={index === hierarchies.length - 1}>
            {hierarchy.statuses.map((status) => {
              const config = WORKFLOW_STATUS_CONFIG[status.statusPreview as WorkflowStatusCode]

              if (!config) return null

              const isLoading = loadingStatus === status.statusPreview

              return (
                <S.StatusRow
                  key={status.statusPreview}
                  background={config.background}
                  hoverBackground={config.hoverBackground}
                  color={config.color}
                  badgeColor="#FFFFFF"
                  badgeBackground={config.color}
                  onClick={() => handleStatusClick(status)}
                  style={{ opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto' }}
                >
                  <S.StatusLabel>
                    {config.icon}
                    {config.label}
                  </S.StatusLabel>

                  <S.CountBadge>{status.branchCount}</S.CountBadge>
                </S.StatusRow>
              )
            })}
          </S.BodyColumn>
        ))}
      </S.BodyRow>
    </S.Container>
  )
}
